import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Meldet Seitenaufrufe der **öffentlichen** Auftritte an Umami.
 *
 * Warum von Hand und nicht automatisch: Die interne Anwendung und die Website
 * liegen im selben Router (`App.tsx`), und die Routen der Anwendung sind nicht
 * an einen Host gebunden. Umamis eingebaute Zählung nimmt deshalb alles mit —
 * `/dashboard`, `/anfragen`, `/select-system`, jeden Klick der eigenen Leute.
 *
 * In den 30 Tagen vor dem 31.08.2026 waren das 191 von 425 Ereignissen, also
 * fast die Hälfte. Die Frage „wie viele Besucher hatte die Website" ließ sich
 * damit nicht beantworten, und niemandem wäre das an den Zahlen aufgefallen.
 *
 * Deshalb steht `data-auto-track="false"` in `src/index.tsx`, und gezählt wird
 * nur dort, wo dieser Haken hängt: in `SiteLayout` und `M38Layout`, den Rahmen
 * der beiden öffentlichen Auftritte. Was außerhalb liegt, ist Arbeit und kein
 * Besuch.
 *
 * Kommt eine öffentliche Seite dazu, die außerhalb dieser beiden Rahmen liegt,
 * gehört der Haken auch dorthin — sonst fehlt sie in der Statistik, lautlos.
 *
 * **Angemeldete Personen zählen nicht.** Wer ein `accessToken` im Browser hat,
 * arbeitet hier — auch dann, wenn er gerade auf der Startseite steht, um von
 * dort zum Login zu gehen. Ohne diese Grenze wäre jeder Arbeitsbeginn ein
 * Website-Besuch. Der Weg ist bewusst der Anmeldezustand und nicht der Pfad:
 * Der Klick auf „Login" beginnt auf einer öffentlichen Seite, und die zählt
 * sonst mit, obwohl niemand die Website besucht hat.
 *
 * Was das nicht abdeckt: eine Mitarbeiterin, die abgemeldet auf der Website
 * liest. Das ist ein echter Besuch von außen und bleibt drin. Wer sich auch
 * dabei heraushalten will, setzt einmal je Gerät in der Browser-Konsole
 * `localStorage.setItem('umami.disabled', '1')` — Umamis eigener Schalter.
 */
export default function useUmamiPageview(): void {
  const { pathname, search } = useLocation();

  useEffect(() => {
    try {
      // Angemeldet heißt: an der Arbeit, nicht zu Besuch.
      if (localStorage.getItem('accessToken')) return;

      const umami = (window as any).umami;
      // Fehlt Umami — geblockt, Netz weg, Prerendering —, passiert nichts.
      // Eine ausgefallene Messung darf die Seite nie stören.
      if (typeof umami?.track === 'function') {
        umami.track((props: Record<string, unknown>) => ({
          ...props,
          url: `${pathname}${search}`,
        }));
      }
    } catch {
      /* Messung ist nie wichtiger als die Seite. */
    }
  }, [pathname, search]);
}
